import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { IoClose } from "react-icons/io5";

import { handleError } from "src/Common/function";
import BranchService from "src/Services/API/branch.service";
import FormTemplateService from "src/Services/API/formtemplate.service";
import { DEFAULT_FORM_ICON, FORM_ICONS } from "src/Pages/forms/formIcons";

/**
 * Choose the branches a template is a Standard Form in.
 *
 * The branch set *is* the membership — there is no separate on/off switch. Pick
 * branches and the template joins the library for exactly those; clear them all
 * and it leaves the library. There is deliberately no implicit "all branches":
 * availability is always an explicit choice.
 *
 * Select all / Clear all are conveniences over the same list, not a distinct
 * "every branch, including ones created later" state — a branch added next month
 * will not inherit membership.
 */
const StandardBranchesModal = ({ template, onClose, onSaved }) => {
  const [branches, setBranches] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  // Only Standard Forms carry an icon — it exists to tell apart a list of
  // near-identical rows in the field — so it is set here rather than in the
  // general template editor.
  const [icon, setIcon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    BranchService.getAll()
      .then((response) => setBranches(response.data || []))
      .catch((error) => handleError(error))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setSelectedIds((template?.standardBranches || []).map((branch) => branch.id));
    setIcon(template?.icon ?? null);
  }, [template]);

  const toggle = (branchId) => {
    setSelectedIds((current) =>
      current.includes(branchId)
        ? current.filter((id) => id !== branchId)
        : [...current, branchId],
    );
  };

  const handleSave = () => {
    setSaving(true);
    FormTemplateService.setStandardBranches(template.id, selectedIds, icon)
      .then(() => {
        toast(
          selectedIds.length
            ? `Standard in ${selectedIds.length} branch${selectedIds.length === 1 ? "" : "es"}`
            : "Removed from Standard Templates",
        );
        onSaved();
      })
      .catch((error) => handleError(error))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <OutsideClickHandler onOutsideClick={onClose}>
        <div className="w-[460px] max-h-[80vh] bg-white rounded-[6px] flex flex-col">
          <div className="border-b-[1px] border-borderColor py-3 px-4 flex items-center justify-between">
            <div>
              <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor">
                STANDARD FORM BRANCHES
              </p>
              <p className="text-[12px] font-OpenSansRegular text-subTitleColor mt-[2px]">
                {template?.name}
              </p>
            </div>
            <IoClose className="cursor-pointer" onClick={onClose} size={18} />
          </div>

          <div className="px-4 py-3 border-b-[1px] border-borderColor">
            <p className="text-[12px] font-OpenSansRegular text-subTitleColor">
              This form is available on work orders in the branches you pick here, and nowhere
              else. Clearing every branch removes it from Standard Templates.
            </p>
            <div className="flex items-center gap-4 mt-2">
              <button
                onClick={() => setSelectedIds(branches.map((branch) => branch.id))}
                className="text-[11px] font-OpenSansRegular font-[600] text-blueColor"
              >
                Select all
              </button>
              <button
                onClick={() => setSelectedIds([])}
                className="text-[11px] font-OpenSansRegular font-[600] text-subTitleColor"
              >
                Clear all
              </button>
            </div>
          </div>

          <div className="px-4 py-3 border-b-[1px] border-borderColor">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">Icon</p>
            <p className="text-[12px] font-OpenSansRegular text-subTitleColor mb-2">
              Shown beside this form in the field, where several Standard Forms otherwise look
              alike.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {[DEFAULT_FORM_ICON, ...FORM_ICONS].map((option) => {
                const isSelected = icon === option.key;
                return (
                  <div
                    key={option.key || "none"}
                    onClick={() => setIcon(option.key)}
                    title={option.label}
                    className={`w-[38px] h-[38px] rounded-[6px] border flex items-center justify-center cursor-pointer ${
                      isSelected ? "border-blueColor bg-[#EAF3FF]" : "border-borderColor"
                    }`}
                  >
                    <option.Icon size={20} color={option.color} />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <p className="px-4 py-6 text-[12px] font-OpenSansRegular text-subTitleColor text-center">
                Loading…
              </p>
            )}
            {!loading &&
              branches.map((branch) => (
                <label
                  key={branch.id}
                  className="px-4 py-2 border-b border-borderColor flex items-center gap-3 cursor-pointer hover:bg-[#FAFAFB]"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(branch.id)}
                    onChange={() => toggle(branch.id)}
                    className="w-[14px] h-[14px] cursor-pointer"
                  />
                  <span className="text-[13px] font-OpenSansRegular text-[#1A2332]">
                    {branch.name}
                  </span>
                </label>
              ))}
            {!loading && branches.length === 0 && (
              <p className="px-4 py-6 text-[12px] font-OpenSansRegular text-subTitleColor text-center">
                No branches
              </p>
            )}
          </div>

          <div className="p-4 flex items-center gap-3 border-t-[1px] border-borderColor">
            <div
              onClick={onClose}
              className="flex-1 h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer font-OpenSansRegular font-[600]"
            >
              CANCEL
            </div>
            <div
              onClick={() => !saving && handleSave()}
              className={`flex-1 h-[30px] text-[10px] rounded-[6px] flex items-center justify-center font-OpenSansRegular font-[600] ${
                saving ? "bg-gray-300 text-white cursor-not-allowed" : "bg-blueColor text-white cursor-pointer"
              }`}
            >
              {saving ? "SAVING..." : "SAVE"}
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default StandardBranchesModal;
